import { useCallback, useMemo, useRef} from 'react'
import { IClientsItem } from '../../../modules/API/Defaults/models/customers'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../store/reducers/rootReducer'
import useDataFetcher from '../../../main/second/mainbar/knisot/hooks/useFetchData'
import { Favorites } from '../../../modules/API/Favorites/favorites'
import { IFavoritesResponse } from '../../../modules/API/Favorites/models/favorites'
import { handleNotifications } from '../../utils/handleNotifications'
import { ICustomerWithFavorites } from '../types/customerWithFavorites'

const useFavoritCustomers = () => {
  const  customerList: IClientsItem[] = useSelector((state: RootState) => state.defaults.clientList.list)
  const {
    data: favorites,
    isLoading: isLoadingFavorites,
    setData: setFavorites
  } = useDataFetcher<IFavoritesResponse>(
    Favorites.getFavoritesByObjectType, "10"
  )
  
  // Сохраняем отсортированный порядок при первой загрузке
  const sortedOrderRef = useRef<number[] | null>(null)
  

  const createFavorite = useCallback(async (customerId: number) => {
    if(!favorites) return
    try {
      await Favorites.createFavorite({
        ObjectType: 10,
        ObjectID: customerId
      })

      setFavorites([...favorites, customerId])
      handleNotifications("נוסף למועדפים", {type: "success"})
    } catch (error) {
      handleNotifications("משהו השתבש", {type: "error"})
    }
  }, [favorites, setFavorites])

  const deleteFavorite = useCallback(async (customerId: number) => {
    if(!favorites) return
    try {
      await Favorites.deleteFavorite({
        ObjectType: 10,
        ObjectID: customerId
      })
      setFavorites(favorites.filter((id) => id !== customerId))
      handleNotifications("נמחק מהמועדפים", {type: "success"})
    } catch (error) {
      handleNotifications("משהו השתבש", {type: "error"})
    }
  }, [favorites, setFavorites ])
  const changeOrder = useCallback(async (draggedItem: ICustomerWithFavorites, newIndex: number) => {
    if(!favorites || !sortedOrderRef.current) return
    
    // Находим текущий индекс элемента в favorites
    const oldIndex = favorites.indexOf(draggedItem.CustomerID)
    if (oldIndex === -1) return // Элемент не найден
    
    const newFavorites = [...favorites]
    newFavorites.splice(oldIndex, 1)
    newFavorites.splice(newIndex, 0, draggedItem.CustomerID)
    
    const newSortedOrder = [...sortedOrderRef.current]
    
    // Находим позицию перетаскиваемого элемента в общем списке
    const oldPositionInSorted = newSortedOrder.indexOf(draggedItem.CustomerID)
    if (oldPositionInSorted !== -1) {
      newSortedOrder.splice(oldPositionInSorted, 1)
      newSortedOrder.splice(newIndex, 0, draggedItem.CustomerID)
    }
    
    // Fallback values
    const oldFavorites = favorites
    const oldSortedOrder = sortedOrderRef.current
    

    sortedOrderRef.current = newSortedOrder
    setFavorites(newFavorites)
    
    try {
      await Favorites.changeOrder(newFavorites)
      handleNotifications("הסדר עודכן", {type: "success"})
    } catch (error) {
      handleNotifications("הסדר לא עודכן", {type: "error"})
      // Откатываем изменения при ошибке
      setFavorites(oldFavorites)
      sortedOrderRef.current = oldSortedOrder
    }
  }, [favorites, setFavorites])
  const favoritesList : ICustomerWithFavorites[] = useMemo(() => {
     if(!favorites) return []
     
     // Добавляем флаг isFavorite к каждому клиенту
     const customersWithFavorites = customerList.map((customer) => {
      const isFavorite = favorites.includes(customer.CustomerID)
      return {
        ...customer,
        isFavorite: isFavorite
      }
     })
     
     // Если порядок еще не был сохранен, сортируем и сохраняем
     if (!sortedOrderRef.current) {
       const favoriteIds = favorites
       const favoriteSet = new Set(favoriteIds)
       const existingIdsSet = new Set(customersWithFavorites.map(c => c.CustomerID))

       const initialOrder: number[] = []

       // 1. Добавляем избранных в порядке массива favorites
       favoriteIds.forEach((id) => {
         if (existingIdsSet.has(id)) {
           initialOrder.push(id)
         }
       })

       // 2. Добавляем остальных клиентов в порядке customerList
       customersWithFavorites.forEach((customer) => {
         if (!favoriteSet.has(customer.CustomerID)) {
           initialOrder.push(customer.CustomerID)
         }
       })

       sortedOrderRef.current = initialOrder
     }
     
     // Создаем мапу для быстрого доступа по CustomerID
     const customersMap = new Map(
       customersWithFavorites.map(c => [c.CustomerID, c])
     )
     // Возвращаем список в сохраненном порядке, обновляя только флаги
     return sortedOrderRef.current
       .map(id => customersMap.get(id))
       .filter((c): c is ICustomerWithFavorites => c !== undefined)
     
  }, [customerList, favorites])
  
  return (
    {
      list: favoritesList,
      isLoading: isLoadingFavorites,
      createFavorite,
      deleteFavorite,
      changeOrder
    }
  )
}

export default useFavoritCustomers